import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Reveal, MaskedHeading } from "../Reveal";
import { PASSAGES } from "../../data/content";
import { track } from "../../lib/analytics";

const toneClasses = {
  abyss: { bg: "bg-abyss", text: "text-pearl", sub: "text-pearl/70", num: "text-aqua/25" },
  marine: { bg: "bg-marine", text: "text-pearl", sub: "text-pearl/70", num: "text-aqua/25" },
  sand: { bg: "bg-sand", text: "text-abyss", sub: "text-abyss/70", num: "text-turquoise/25" },
};

const PassageBlock = ({ p, index }) => {
  const reduced = useReducedMotion();
  const t = toneClasses[p.tone];
  const reversed = index % 2 === 1;

  return (
    <div className={`${t.bg} py-20 md:py-32 relative overflow-hidden`} data-testid={`passage-${p.num}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-center ${reversed ? "lg:[direction:rtl]" : ""}`}>
          <div className="lg:[direction:ltr]">
            <div className="flex items-baseline gap-5">
              <span className={`font-display font-medium text-[clamp(4rem,10vw,9rem)] leading-none ${t.num}`}>
                {p.num}
              </span>
              <div>
                <MaskedHeading
                  tag="h3"
                  className={`font-display font-medium text-[clamp(2.4rem,5vw,4rem)] leading-none ${t.text}`}
                  lines={[p.title]}
                />
              </div>
            </div>
            <p className={`mt-3 eyebrow ${p.tone === "sand" ? "text-turquoise" : "text-aqua"}`}>
              {p.subtitle}
            </p>

            <div className={`mt-7 space-y-4 max-w-xl ${t.sub} text-base leading-relaxed`}>
              {p.paragraphs.map((para, i) => (
                <Reveal key={i} delay={i * 0.05} as="p">
                  {para}
                </Reveal>
              ))}
            </div>

            <ul className={`mt-8 space-y-3 max-w-xl`}>
              {p.items.map((it, i) => (
                <Reveal key={i} delay={0.1 + i * 0.06} as="li" className="flex gap-3 items-start">
                  <span className={`mt-2 w-6 h-px ${p.tone === "sand" ? "bg-turquoise" : "bg-aqua"} shrink-0`} />
                  <span className={`${t.text} text-[0.98rem] leading-snug`}>{it}</span>
                </Reveal>
              ))}
            </ul>

            <p className={`mt-8 text-sm italic ${t.sub}`}>{p.ambiance}</p>
          </div>

          <motion.div
            className="lg:[direction:ltr] overflow-hidden rounded-sm"
            initial={reduced ? { opacity: 0 } : { opacity: 0, scale: 1.06 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-12%" }}
            transition={{ duration: 1.3, ease: [0.16, 1, 0.3, 1] }}
          >
            <img
              src={p.image}
              alt={`${p.title} — ${p.subtitle}`}
              loading="lazy"
              className="w-full aspect-[4/5] object-cover"
            />
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default function Passages() {
  return (
    <section id="passages" className="section" data-testid="passages-section">
      <div className="bg-abyss py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <span className="chapter-num">CHAPITRE 04 — LES TROIS PASSAGES</span>
          <MaskedHeading
            tag="h2"
            className="font-display font-medium text-[clamp(2.4rem,6vw,5rem)] leading-none tracking-tight text-pearl mt-6"
            lines={["Trois passages,", "comme trois actes."]}
          />
        </div>
      </div>

      {PASSAGES.map((p, i) => (
        <PassageBlock key={p.num} p={p} index={i} />
      ))}

      <div className="bg-sand py-16 md:py-24 text-center">
        <Reveal className="max-w-3xl mx-auto px-6">
          <a
            href="#final-form"
            className="btn btn--dark"
            data-testid="enter-traversee-cta"
            onClick={() => track("secondary_cta_click", { target: "final-form" })}
          >
            JE CHOISIS D'ENTRER DANS LA TRAVERSÉE
            <ArrowRight size={18} strokeWidth={1.8} />
          </a>
        </Reveal>
      </div>
    </section>
  );
}
